import React, { useState, useEffect, useRef } from 'react';
import DocumentSummary from './DocumentSummary';
import { generateMockCitations } from '../utils/mockCitations';
import SnippetPanel from './SnippetPanel';
import { generateClause, generateSnippets } from '../utils/mockClauseGenerator';
import { generateQualityScore } from '../utils/qualityScore';
import { generateMockResponse } from '../utils/mockResponseGenerator';
import { FileText, Download } from 'lucide-react';
import { Send, User, Bot, Copy, ThumbsUp, ThumbsDown, RefreshCw, Edit2, X, Check, Sparkles, MoreHorizontal, ArrowRight, ArrowLeft } from 'lucide-react';

const ChatInterface = ({ selectedFile, preconstructedReplies = [], uploadedDoc = null }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [showConfidence, setShowConfidence] = useState(true)
  const [showReferences, setShowReferences] = useState(true)
  const [persona, setPersona] = useState('Analyst')
  const [draftMode, setDraftMode] = useState('Draft Clause')
  const [snippets, setSnippets] = useState([])
  const [quickEditIndex, setQuickEditIndex] = useState(-1)
  const [quickEditText, setQuickEditText] = useState('')
  const [usedQuickPromptIndex, setUsedQuickPromptIndex] = useState(null)

  // Alternatives UI state
  const [messageAlternatives, setMessageAlternatives] = useState({})
  const [altExpanded, setAltExpanded] = useState([])
  const [altActiveIndex, setAltActiveIndex] = useState({})

  // Editing & undo state
  const [editingMessageId, setEditingMessageId] = useState(null)
  const [editBuffer, setEditBuffer] = useState('')
  const [originalBackups, setOriginalBackups] = useState({})
  const [selectedAlternative, setSelectedAlternative] = useState({})

  // Smart suggestions
  const [smartSuggestions, setSmartSuggestions] = useState([])
  const smartDebounceRef = useRef(null)

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    try { if (inputRef.current) inputRef.current.focus() } catch (e) { }
  }, [messages]);

  useEffect(() => {
    if (!inputMessage || inputMessage.length < 3) { setSmartSuggestions([]); return }
    if (smartDebounceRef.current) clearTimeout(smartDebounceRef.current)
    smartDebounceRef.current = setTimeout(() => {
      try {
        const s1 = `${inputMessage} Rephrase concisely for ${persona}`
        const s2 = `${inputMessage} Expand and include next steps for ${draftMode}`
        const r1 = generateMockResponse(s1, uploadedDoc?.fileName || selectedFile, uploadedDoc?.tone || 'Formal', uploadedDoc?.replyLength || 'Medium', uploadedDoc?.metadata || {})
        const r2 = generateMockResponse(s2, uploadedDoc?.fileName || selectedFile, uploadedDoc?.tone || 'Formal', uploadedDoc?.replyLength || 'Medium', uploadedDoc?.metadata || {})
        setSmartSuggestions([r1.answer, r2.answer].filter(Boolean))
      } catch (e) { setSmartSuggestions([]) }
    }, 300)
    return () => { if (smartDebounceRef.current) clearTimeout(smartDebounceRef.current) }
  }, [inputMessage, persona, draftMode, uploadedDoc])

  useEffect(() => {
    if (uploadedDoc) {
      const s = generateSnippets({ fileName: uploadedDoc.fileName, metadata: uploadedDoc.metadata })
      setSnippets(s)
    } else {
      setSnippets([])
    }
  }, [uploadedDoc])

  const streamAIMessage = async (fullMessage, { wordDelay = 30 } = {}) => {
    const placeholder = { id: fullMessage.id, text: '', sender: 'ai', timestamp: fullMessage.timestamp, isStreaming: true }
    setMessages(prev => [...prev, placeholder])

    const words = (fullMessage.text || '').split(/(\s+)/)
    let current = ''
    for (let i = 0; i < words.length; i++) {
      current += words[i]
      setMessages(prev => prev.map(m => m.id === placeholder.id ? { ...m, text: current } : m))
      const delay = wordDelay + Math.floor(Math.random() * 20)
      await new Promise(r => setTimeout(r, delay))
    }

    setMessages(prev => prev.map(m => m.id === placeholder.id ? { ...fullMessage, isStreaming: false } : m))
  }

  const addAIMessage = (aiMessage) => {
    streamAIMessage(aiMessage, { wordDelay: 30 })
  }

  const determineRiskTag = ({ tone = 'Formal', replyLength = 'Medium', complexity = 'Medium' } = {}) => {
    const t = (tone || '').toLowerCase()
    const r = replyLength
    if (complexity === 'High') return 'Requires Legal Review'
    if (r === 'Short') return 'Low'
    if (r === 'Medium') return t === 'formal' ? 'Moderate' : 'Low'
    if (r === 'Long') return t === 'formal' ? 'High' : 'Moderate'
    return 'Moderate'
  }

  const generateReasoningTrace = ({ answer = '', question = '', metadata = {}, uploadedDoc = null, complexity = 'Medium' } = {}) => {
    const q = (question || '').toLowerCase()
    const a = (answer || '').toLowerCase()
    let issue = 'General inquiry'
    if (q.includes('penalt') || a.includes('penalt') || q.includes('fine')) issue = 'Potential penalties/compliance gaps'
    else if (q.includes('definition') || a.includes('definition')) issue = 'Terminology/definitions clarification'
    else if (q.includes('register') || a.includes('registration')) issue = 'Registration/licensing requirements'
    else if (q.includes('bank') || q.includes('financial') || a.includes('capital')) issue = 'Financial/institutional obligations'

    const jurisdictionApplied = metadata?.jurisdiction || uploadedDoc?.metadata?.jurisdiction || 'Generic jurisdiction'
    let clauseType = 'General provisions'
    if (a.includes('definition')) clauseType = 'Definitions'
    else if (a.includes('penalt') || a.includes('criminal') || a.includes('fines')) clauseType = 'Enforcement & penalties'
    else if (a.includes('compli') || a.includes('report')) clauseType = 'Compliance & reporting'
    else if (a.includes('registration') || a.includes('licens')) clauseType = 'Registration/licensing'

    let recommendedAction = ''
    if (complexity === 'High') recommendedAction = 'Recommend immediate legal review and updates to controls.'
    else if (issue.includes('penalties')) recommendedAction = 'Review penalty provisions and update compliance program.'
    else if (issue.includes('Terminology')) recommendedAction = 'Clarify definitions in reporting templates.'
    else recommendedAction = 'Document review and operational checklist; escalate if uncertain.'

    return { issueIdentified: issue, jurisdictionApplied, clauseTypeDetected: clauseType, recommendedAction }
  }

  const [expandedTraces, setExpandedTraces] = useState([])
  const toggleTrace = (id) => {
    setExpandedTraces(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = {
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString(),
      id: Math.random().toString(36).slice(2, 9)
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const activePrompts = uploadedDoc?.preconstructedReplies || preconstructedReplies
      const tone = uploadedDoc?.tone || 'Formal'
      const length = uploadedDoc?.replyLength || 'Medium'
      const metadata = uploadedDoc?.metadata || {}

      await new Promise(r => setTimeout(r, 800));

      const { answer, refs, confidence } = generateMockResponse(inputMessage, uploadedDoc?.fileName || selectedFile, tone, length, metadata)

      let citations = []
      if (showReferences) {
        const mockCits = generateMockCitations({ jurisdiction: metadata?.jurisdiction, year: metadata?.year })
        citations = mockCits.map(c => ({ text: c.text, id: c.id }))
      }

      const complexity = uploadedDoc?.summary?.complexityRating || (metadata?.complexityRating) || 'Medium'
      const riskTag = determineRiskTag({ tone, replyLength: length, complexity })
      const reasoningTrace = generateReasoningTrace({ answer, question: inputMessage, metadata, uploadedDoc, complexity })

      const aiMessage = {
        id: Math.random().toString(36).slice(2, 9),
        text: answer,
        sender: 'ai',
        timestamp: new Date().toISOString(),
        confidence,
        riskTag,
        reasoningTrace,
        qualityScore: generateQualityScore({ tone, mode: draftMode, persona, complexity }),
        references: [
          ...refs.map(r => ({ title: r.title, section_num: r.section_num })),
          ...citations,
        ],
        docName: uploadedDoc?.fileName || selectedFile || ''
      }
      addAIMessage(aiMessage)
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        text: `Sorry, there was an error processing your request: ${error.message}`,
        sender: 'ai',
        timestamp: new Date().toISOString(),
        isError: true,
        id: Math.random().toString(36).slice(2, 9)
      };
      setMessages(prev => [...prev, errorMessage]);
    }

    setIsLoading(false);
    setInputMessage('');
  };

  const handleQuickReply = async (prompt) => {
    const userMessage = { text: prompt, sender: 'user', timestamp: new Date().toISOString(), id: Math.random().toString(36).slice(2, 9) }
    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)
    await new Promise(r => setTimeout(r, 800))

    const tone = uploadedDoc?.tone || 'Formal'
    const length = uploadedDoc?.replyLength || 'Medium'
    const metadata = uploadedDoc?.metadata || {}
    const { answer, refs, confidence } = generateMockResponse(prompt, uploadedDoc?.fileName || selectedFile, tone, length, metadata)

    let citations = []
    if (showReferences) {
      const mockCits = generateMockCitations({ jurisdiction: metadata?.jurisdiction, year: metadata?.year })
      citations = mockCits.map(c => ({ text: c.text, id: c.id }))
    }

    const complexity = uploadedDoc?.summary?.complexityRating || 'Medium'
    const riskTag = determineRiskTag({ tone, replyLength: length, complexity })
    const reasoningTrace = generateReasoningTrace({ answer, question: prompt, metadata, uploadedDoc, complexity })

    const aiMessage = { id: Math.random().toString(36).slice(2, 9), text: answer, sender: 'ai', timestamp: new Date().toISOString(), confidence, riskTag, reasoningTrace, qualityScore: generateQualityScore({ tone, mode: draftMode, persona, complexity }), references: [...refs.map(r => ({ title: r.title, section_num: r.section_num })), ...citations], docName: uploadedDoc?.fileName || selectedFile || '' }

    setUsedQuickPromptIndex(null)
    setUsedQuickPromptIndex(quickEditIndex >= 0 ? quickEditIndex : null)
    addAIMessage(aiMessage)
    setIsLoading(false)
  }

  const handleSnippetSelect = async (snippet) => {
    setMessages(prev => [...prev, { text: `Inspect snippet: ${snippet.title}`, sender: 'user', timestamp: new Date().toISOString(), id: Math.random().toString(36).slice(2, 9) }])
    setIsLoading(true)
    await new Promise(r => setTimeout(r, 800))

    const tone = uploadedDoc?.tone || 'Formal'
    const length = uploadedDoc?.replyLength || 'Medium'
    const metadata = uploadedDoc?.metadata || {}

    const personaPrefix = persona === 'General Counsel' ? 'Counsel:' : persona === 'Compliance Officer' ? 'Compliance:' : persona === 'Regulator' ? 'Regulator:' : persona === 'Academic' ? 'Academic:' : ''
    let explanation = `${personaPrefix} ${snippet.text} -- ${draftMode}.`
    if (draftMode === 'Rewrite') explanation = `${personaPrefix} Rewritten: ${snippet.text}`
    if (draftMode === 'Explain') explanation = `${personaPrefix} Explanation: ${snippet.text} (key points and implications)`

    const mockCits = showReferences ? generateMockCitations({ jurisdiction: metadata?.jurisdiction || '', year: metadata?.year || '' }) : []
    const citations = mockCits.map(c => ({ text: c.text, id: c.id }))
    const complexity = uploadedDoc?.summary?.complexityRating || 'Medium'
    const riskTag = determineRiskTag({ tone, replyLength: length, complexity })
    const reasoningTrace = generateReasoningTrace({ answer: explanation, question: snippet.title, metadata, uploadedDoc, complexity })

    const aiMessage = { id: Math.random().toString(36).slice(2, 9), text: explanation, sender: 'ai', timestamp: new Date().toISOString(), confidence: 0.8, riskTag, reasoningTrace, qualityScore: generateQualityScore({ tone, mode: draftMode, persona, complexity }), references: citations, docName: uploadedDoc?.fileName || '' }
    addAIMessage(aiMessage)
    setIsLoading(false)
  }

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); alert('Copied to clipboard') } catch (e) { alert('Copy failed') }
  }

  return (
    <div className="flex flex-col h-full bg-gray-50/50">
      {/* Header / Controls */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shadow-sm z-10">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-bold text-gray-900">{uploadedDoc?.fileName || selectedFile || 'New Chat'}</h2>
            {uploadedDoc?.metadata?.jurisdiction && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                {uploadedDoc.metadata.jurisdiction}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
            <span>{persona} Mode</span>
            <span>•</span>
            <span>{draftMode}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 p-1 rounded-lg">
            {['Analyst', 'Counsel', 'Regulator'].map(p => (
              <button
                key={p}
                onClick={() => setPersona(p)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${persona === p ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                {p}
              </button>
            ))}
          </div>

          <div className="h-6 w-px bg-gray-200 mx-1"></div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => alert('Exporting analysis to PDF... (Mock)')}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Export Analysis"
            >
              <Download size={18} />
            </button>
            <div className="h-6 w-px bg-gray-200 mx-1"></div>
            <label className="flex items-center gap-1.5 cursor-pointer group">
              <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${showConfidence ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}>
                {showConfidence && <Check size={10} className="text-white" />}
              </div>
              <input type="checkbox" checked={showConfidence} onChange={() => setShowConfidence(s => !s)} className="hidden" />
              <span className="text-xs font-medium text-gray-600 group-hover:text-gray-900">Confidence</span>
            </label>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6 scroll-smooth">
        {/* Document Summary */}
        {uploadedDoc?.summary && (
          <DocumentSummary summary={uploadedDoc.summary} title={uploadedDoc.fileName || selectedFile} />
        )}

        {/* Snippets */}
        {snippets && snippets.length > 0 && (
          <SnippetPanel snippets={snippets} onSelect={(s) => handleSnippetSelect(s)} />
        )}

        {/* Messages */}
        {messages.map((message, index) => (
          <div key={index} className={`flex gap-4 ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            {message.sender === 'ai' && (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-sm flex-shrink-0 mt-1">
                <Bot size={16} />
              </div>
            )}

            <div className={`max-w-[80%] space-y-2 ${message.sender === 'user' ? 'items-end flex flex-col' : ''}`}>
              <div className={`p-4 rounded-2xl shadow-sm text-sm leading-relaxed ${message.sender === 'user'
                ? 'bg-blue-600 text-white rounded-tr-none'
                : message.isError
                  ? 'bg-red-50 text-red-700 border border-red-100 rounded-tl-none'
                  : 'bg-white text-gray-800 border border-gray-100 rounded-tl-none'
                }`}>
                {message.text.split('\n').map((line, i) => (
                  <p key={i} className={line.trim() === '' ? 'h-2' : 'mb-1'}>{line}</p>
                ))}
                {message.isStreaming && <span className="inline-block w-1.5 h-4 bg-blue-400 ml-1 animate-pulse align-middle"></span>}
              </div>

              {/* AI Message Metadata */}
              {message.sender === 'ai' && !message.isStreaming && !message.isError && (
                <div className="space-y-3 pl-1">
                  {/* Risk & Confidence Tags */}
                  <div className="flex flex-wrap gap-2 items-center">
                    {message.riskTag && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${message.riskTag === 'Low' ? 'bg-green-50 text-green-700 border-green-100' :
                        message.riskTag === 'Moderate' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' :
                          'bg-red-50 text-red-700 border-red-100'
                        }`}>
                        Risk: {message.riskTag}
                      </span>
                    )}
                    {showConfidence && message.confidence && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                        Confidence: {(message.confidence * 100).toFixed(0)}%
                      </span>
                    )}
                    {message.reasoningTrace && (
                      <button
                        onClick={() => toggleTrace(message.id)}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                      >
                        <Sparkles size={12} />
                        {expandedTraces.includes(message.id) ? 'Hide Reasoning' : 'View Reasoning'}
                      </button>
                    )}
                  </div>

                  {/* Reasoning Trace */}
                  {expandedTraces.includes(message.id) && message.reasoningTrace && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-xs space-y-2 animate-in fade-in slide-in-from-top-2">
                      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                        <span className="font-semibold text-gray-500">Issue:</span>
                        <span className="text-gray-800">{message.reasoningTrace.issueIdentified}</span>
                        <span className="font-semibold text-gray-500">Clause:</span>
                        <span className="text-gray-800">{message.reasoningTrace.clauseTypeDetected}</span>
                        <span className="font-semibold text-gray-500">Action:</span>
                        <span className="text-gray-800">{message.reasoningTrace.recommendedAction}</span>
                      </div>
                    </div>
                  )}

                  {/* References */}
                  {showReferences && message.references && message.references.length > 0 && (
                    <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100/50">
                      <div className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1">
                        <FileText size={12} />
                        References
                      </div>
                      <div className="space-y-1">
                        {message.references.map((ref, i) => (
                          <div key={i} className="text-xs text-blue-700 flex items-start gap-2">
                            <span className="mt-1 w-1 h-1 bg-blue-400 rounded-full flex-shrink-0"></span>
                            <span>{ref.text || `${ref.title}: Section ${ref.section_num}`}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-1">
                    <button onClick={() => copyToClipboard(message.text)} className="text-gray-400 hover:text-gray-600 transition-colors" title="Copy">
                      <Copy size={14} />
                    </button>
                    <button className="text-gray-400 hover:text-green-600 transition-colors" title="Helpful">
                      <ThumbsUp size={14} />
                    </button>
                    <button className="text-gray-400 hover:text-red-600 transition-colors" title="Not Helpful">
                      <ThumbsDown size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {message.sender === 'user' && (
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 flex-shrink-0 mt-1">
                <User size={16} />
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200 p-4">
        {/* Quick Prompts */}
        {(uploadedDoc?.preconstructedReplies || preconstructedReplies) && messages.length < 2 && (
          <div className="flex gap-2 overflow-x-auto pb-3 mb-2 no-scrollbar">
            {(uploadedDoc?.preconstructedReplies || preconstructedReplies).map((p, i) => (
              <button
                key={i}
                onClick={() => handleQuickReply(p)}
                className="flex-shrink-0 px-4 py-2 bg-gray-50 hover:bg-blue-50 text-gray-600 hover:text-blue-700 text-sm rounded-full border border-gray-200 hover:border-blue-200 transition-all whitespace-nowrap"
              >
                {p}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="relative max-w-4xl mx-auto">
          <input
            ref={inputRef}
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder={`Ask about ${uploadedDoc?.fileName || 'the document'}...`}
            className="w-full pl-4 pr-12 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all shadow-sm text-gray-800 placeholder-gray-400"
          />
          <button
            type="submit"
            disabled={!inputMessage.trim() || isLoading}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all ${inputMessage.trim() && !isLoading
              ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
          >
            <Send size={18} />
          </button>
        </form>
        <div className="text-center mt-2 text-xs text-gray-400">
          AI can make mistakes. Verify important information.
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;